"use client"

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { ComponentProps, FormEvent } from "react"
import { useEffect, useState } from "react"
import { toast } from "sonner"
import { getCartItems, updateCartShippingAddress } from "@/api/cart"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/utilities"

const selectClassName = cn(
  "flex h-10 w-full min-w-0 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground/70",
  "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
)

interface CartShippingSectionProps extends ComponentProps<"section"> {}

export const CartShippingSection = ({
  className,
  ...props
}: CartShippingSectionProps) => {
  const queryClient = useQueryClient()

  const cartQuery = useQuery({
    queryKey: ["cart", "items"],
    queryFn: getCartItems,
    retry: false,
  })

  const address = cartQuery.data?.shippingAddress

  const [country, setCountry] = useState("BR")
  const [state, setState] = useState("SP")
  const [city, setCity] = useState("")
  const [postcode, setPostcode] = useState("")

  useEffect(() => {
    setCountry(address?.country || "BR")
    setState(address?.state || "SP")
    setCity(address?.city || "")
    setPostcode(address?.postcode || "")
  }, [address?.country, address?.state, address?.city, address?.postcode])

  const updateShippingAddressMutation = useMutation({
    mutationFn: updateCartShippingAddress,
    onMutate: () => {
      const toastId = toast.loading("Atualizando endereco de entrega...")
      return { toastId }
    },
    onError: (_error, _variables, context) => {
      toast.error("Nao foi possivel atualizar o endereco de entrega.", {
        id: context?.toastId,
      })
    },
    onSuccess: (response, _variables, context) => {
      if (!response.ok) {
        toast.error(
          response.message?.trim() ||
            "Nao foi possivel atualizar o endereco de entrega.",
          {
            id: context?.toastId,
          }
        )
        return
      }

      toast.success(response.message?.trim() || "Endereco atualizado.", {
        id: context?.toastId,
      })
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ["cart", "items"] })
      await queryClient.invalidateQueries({
        queryKey: ["checkout", "order-review"],
      })
    },
  })

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    updateShippingAddressMutation.mutate({
      country,
      state,
      city,
      postcode,
    })
  }

  return (
    <section
      className={cn(
        "space-y-4 rounded-2xl border bg-white p-5 shadow-xs",
        className
      )}
      {...props}
    >
      <header className='flex w-full items-center justify-between'>
        <h2 className='text-left font-semibold text-xl'>Calcular entrega</h2>
      </header>

      <form className='space-y-3' onSubmit={handleSubmit}>
        <div className='space-y-1'>
          <label className='text-muted-foreground text-sm' htmlFor='country'>
            Country / region
          </label>
          <select
            className={selectClassName}
            id='country'
            onChange={(event) => setCountry(event.target.value)}
            value={country}
          >
            <option value='BR'>Brasil</option>
          </select>
        </div>

        <div className='space-y-1'>
          <label className='text-muted-foreground text-sm' htmlFor='state'>
            Estado <span className='text-destructive'>*</span>
          </label>
          <select
            className={selectClassName}
            id='state'
            onChange={(event) => setState(event.target.value)}
            required
            value={state}
          >
            <option value='SP'>Sao Paulo</option>
          </select>
        </div>

        <div className='space-y-1'>
          <label className='text-muted-foreground text-sm' htmlFor='city'>
            Cidade: <span className='text-destructive'>*</span>
          </label>
          <Input
            className='h-10 rounded-md'
            id='city'
            onChange={(event) => setCity(event.target.value)}
            required
            value={city}
          />
        </div>

        <div className='space-y-1'>
          <label className='text-muted-foreground text-sm' htmlFor='postcode'>
            Codigo postal / CEP: <span className='text-destructive'>*</span>
          </label>
          <Input
            className='h-10 rounded-md'
            id='postcode'
            onChange={(event) => setPostcode(event.target.value)}
            required
            value={postcode}
          />
        </div>

        <Button
          aria-busy={updateShippingAddressMutation.isPending}
          disabled={updateShippingAddressMutation.isPending}
          type='submit'
          variant='outline'
        >
          {updateShippingAddressMutation.isPending
            ? "Atualizando..."
            : "Atualizar"}
        </Button>
      </form>
    </section>
  )
}
